import type { HTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { InfoTip } from "./info-tip";

/** Karta: 20px radius, yumshoq soya (tunda faqat ingichka halqa) -- redesign v2 */
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("rounded-card border border-border/60 bg-surface shadow-card", className)} {...props} />;
}

export function CardHeader({ title, action, className, subtitle }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-start justify-between gap-3 px-5 pt-5 pb-2", className)}>
      <div className="min-w-0">
        <h3 className="text-[17px] leading-snug font-semibold tracking-[-0.01em]">{title}</h3>
        {subtitle && <div className="mt-0.5 text-[13px] text-muted">{subtitle}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("px-5 pb-5", className)} {...props} />;
}

type Tone = "muted" | "danger" | "success" | "warning" | "accent" | "info";

const chipTones: Record<Tone, string> = {
  muted: "bg-fill text-ink",
  danger: "bg-danger-soft text-danger",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
  accent: "bg-accent-soft text-ink",
  info: "bg-primary-soft text-ink",
};
const textTones: Record<Tone, string> = {
  muted: "text-muted",
  danger: "text-danger",
  success: "text-success",
  warning: "text-warning",
  accent: "text-ink",
  info: "text-ink",
};

/**
 * Ko'rsatkich kartasi: katta yengil raqam (Tesla/iPhone ruhi), ostida chip + izoh.
 * `sub` -- izoh matni (eski chaqiruvlar o'zgarmaydi), `chip` -- qisqa belgi ("▲ 12", "2 waiting").
 */
export function KpiCard({
  label,
  value,
  sub,
  accent,
  subTone = "muted",
  chip,
  chipTone = "muted",
  size = "md",
  valueTone,
  className,
  to,
  onClick,
  info,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  accent?: "budget";
  subTone?: Tone;
  chip?: ReactNode;
  chipTone?: Tone;
  size?: "md" | "lg";
  valueTone?: "danger" | "success";
  className?: string;
  /** Ko'rsatkich ortidagi ro'yxatga havola (foydalanuvchi qoidasi 2026-10-18: raqam ko'rsatilsa, bosib batafsilini ko'rish mumkin) */
  to?: string;
  /** Sahifa ichida filtr qo'yish kabi amal (havola o'rniga) */
  onClick?: () => void;
  /** Ko'rsatkich izohi (yorliq yonida "i", hover'da) -- matn `metrics` namespace'idan (2026-10-24) */
  info?: ReactNode;
}) {
  const interactive = !!to || !!onClick;
  const body = (
    <Card
      className={cn(
        "relative flex flex-col gap-2 px-5 py-4",
        accent === "budget" && "border-budget/30 bg-budget-soft/40",
        interactive && "transition-[border-color,box-shadow] duration-150 group-hover:border-border group-hover:shadow-sm group-focus-visible:ring-2 group-focus-visible:ring-accent/40",
        className,
      )}
    >
      {interactive && <ArrowUpRight size={14} strokeWidth={1.75} aria-hidden className="absolute top-4 right-4 text-faint transition-colors group-hover:text-ink" />}
      <div className={cn("text-[13px] font-medium text-muted", interactive && "pr-5")}>
        {label}
        {info && <InfoTip text={info} className="ml-1.5" />}
      </div>
      <div className={cn("tabular leading-none font-light tracking-[-0.035em]", size === "lg" ? "text-[52px]" : "text-[36px]", valueTone === "danger" && "text-danger", valueTone === "success" && "text-success")}>
        {value}
      </div>
      {(chip || sub) && (
        <div className="mt-0.5 flex min-h-[20px] flex-wrap items-center gap-2 text-[12px] text-muted">
          {chip && <span className={cn("rounded-full px-2 py-0.5 font-semibold", chipTones[chipTone])}>{chip}</span>}
          {sub && <span className={textTones[subTone]}>{sub}</span>}
        </div>
      )}
    </Card>
  );
  if (to) {
    return (
      <Link to={to} className="group block rounded-card focus-visible:outline-none">
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="group block w-full rounded-card text-left focus-visible:outline-none">
        {body}
      </button>
    );
  }
  return body;
}
